import { type Label, LabelType } from '@piccolohealth/pbs-common';
import type { OnChangeRequest } from '@piccolohealth/ui';
import React from 'react';
import { useCreateLabelMutation } from '../../graphql/hooks/useCreateLabelMutation';
import { useCreateParticipantQRLabelMutation } from '../../graphql/hooks/useCreateParticipantQRLabelMutation';
import { useDeleteParticipantQRLabelMutation } from '../../graphql/hooks/useDeleteParticipantQRLabelMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { usePermission } from '../../hooks/usePermission';
import { LabelsControl } from './LabelsControl';

export interface ManageLabelsQRControlProps {
  participantId: string;
  qrId: string;
  value: string[];
  isDisabled?: boolean;
}

export const ManageParticipantQRLabelsControl = (props: ManageLabelsQRControlProps) => {
  const { qrId, participantId, isDisabled, value } = props;
  const { organization, errorToast } = useAppContext();

  const createLabelMutation = useCreateLabelMutation({ retry: false });
  const createParticipantQRLabelMutation = useCreateParticipantQRLabelMutation({ retry: false });
  const deleteParticipantQRLabelMutation = useDeleteParticipantQRLabelMutation({ retry: false });

  const labelManagePermission = usePermission('manage', 'label');
  const participantQRLabelManagePermission = usePermission('manage', 'participantLabel');

  const onChange = React.useCallback(
    (request: OnChangeRequest<Label>) => {
      switch (request.action) {
        case 'CREATE': {
          return createLabelMutation
            .mutateAsync({
              organizationId: organization.id,
              request: {
                type: LabelType.ParticipantQr,
                color: 'blue',
                name: request.value.value,
              },
            })
            .catch((err: Error) => errorToast(`Error creating label ${err.message}`));
        }
        case 'ADD': {
          return createParticipantQRLabelMutation
            .mutateAsync({
              organizationId: organization.id,
              participantId,
              qrId,
              labelId: request.value.value,
            })
            .catch((err: Error) =>
              errorToast(`Error adding label to participant QR: ${err.message}`),
            );
        }

        case 'REMOVE': {
          return deleteParticipantQRLabelMutation
            .mutateAsync({
              organizationId: organization.id,
              participantId,
              qrId,
              labelId: request.value.value,
            })
            .catch((err: Error) =>
              errorToast(`Error removing label from participant QR: ${err.message}`),
            );
        }
      }
    },
    [
      createLabelMutation,
      organization.id,
      createParticipantQRLabelMutation,
      participantId,
      qrId,
      deleteParticipantQRLabelMutation,
      errorToast,
    ],
  );

  return (
    <LabelsControl
      onChange={onChange}
      value={value}
      isDisabled={isDisabled || !participantQRLabelManagePermission}
      canManage={labelManagePermission}
      variant='selectGhost'
      type={LabelType.ParticipantQr}
    />
  );
};
